import { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";

import { AccessTime } from "@mui/icons-material";
import { Box, FormControlLabel, Stack, Switch, Typography } from "@mui/material";

import { RangeType, useAsu } from "@framework/provider-asu";
import { useSettings } from "@framework/provider-settings";
import { useApiCall } from "@framework/react-hooks";

import { useAlarmPaletteResolver } from "../../../../hooks/use-alarm-color";
import { IAnomalyData } from "../scheme/components/state-and-forecast/interfaces";
import { IDefectsCountDataResponse } from "../scheme/components/defects/interfaces";
import { buildForecastWeekStartTimestamp } from "./utils";

interface ITrainPopupResponse {
  train?: Array<{
    WorkTimeRecordedHours?: number | null;
    WorkTimeRecordedDate?: string | null;
  }>;
}

interface IAnomalySummary {
  total: number;
  high: number;
  medium: number;
  low: number;
  worstStatus: number;
}

interface IDefectSummary {
  total: number;
  critical: number;
  medium: number;
  low: number;
  worstStatus: number;
}

const FORECAST_MEDIUM_STATUS = 45;

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const normalizeTrainId = (value: unknown): number | null => {
  const id = Number(value);

  return Number.isFinite(id) && id > 0 ? id : null;
};

const getAnomalySummary = (data: IAnomalyData | null): IAnomalySummary => {
  const summary: IAnomalySummary = {
    total: 0,
    high: 0,
    medium: 0,
    low: 0,
    worstStatus: 0,
  };

  if (!data) {
    return summary;
  }

  summary.total = data.count;
  summary.high = data.l3;
  summary.medium = data.l2;
  summary.low = data.l1;
  summary.worstStatus = data.l3 ? 90 : data.l2 ? FORECAST_MEDIUM_STATUS : data.l1 ? 30 : 0;

  return summary;
};

const getDefectSummary = (data: IDefectsCountDataResponse): IDefectSummary => {
  return {
    total: data.totalCount ?? 0,
    critical: data.critical ?? 0,
    medium: data.medium ?? 0,
    low: data.low ?? 0,
    worstStatus: data.critical > 0 ? 90 : data.medium > 0 ? FORECAST_MEDIUM_STATUS : data.low > 0 ? 30 : 0,
  };
};

const StatusEye: FC<{
  color: string;
  size?: number;
}> = ({ color, size = 14 }) => {
  const normalized = color.trim().toLowerCase();

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        backgroundColor: color,
        border: normalized === "#ffffff" ? "1px solid rgba(95, 100, 114, 0.4)" : "none",
        flexShrink: 0,
      }}
    />
  );
};

const MetricCount: FC<{ color: string; value: number; invert?: boolean }> = ({ color, value, invert = false }) => (
  <Typography
    sx={{
      fontSize: 24,
      lineHeight: "28px",
      fontWeight: 700,
      color,
      minWidth: 12,
      textAlign: "center",
      textShadow: invert ? "0 1px 1px rgba(0, 0, 0, 0.25)" : "none",
    }}
  >
    {value}
  </Typography>
);

const SummaryRow: FC<{
  label: string;
  statusColor: string;
  total: number;
  critical: number;
  medium: number;
  low: number;
}> = ({ label, total, critical, medium, low, statusColor }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
      <StatusEye color={statusColor} />
      <Typography
        sx={{
          color: "#ffffff",
          fontSize: 24,
          lineHeight: "28px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}:
      </Typography>
    </Stack>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <MetricCount color="#ffffff" value={total} invert />
      <MetricCount color={"#f44336"} value={critical} />
      <MetricCount color={"#ffeb3b"} value={medium} />
      <MetricCount color={"#66bb6a"} value={low} />
    </Stack>
  </Stack>
);

const TciScale: FC<{
  isTrendPinned: boolean;
  onTrendPinnedChange: (value: boolean) => void;
  value: number | null;
  warning: number;
  critical: number;
  resolveColor: (status: number) => string;
}> = ({ isTrendPinned, onTrendPinnedChange, value, warning, critical, resolveColor }) => {
  const { formatMessage, formatNumber } = useIntl();

  const normalizedCritical = clamp(critical, 0, 100);
  const normalizedWarning = clamp(warning, normalizedCritical, 100);
  const markerPosition = value === null ? null : clamp(value, 0, 100);
  const segments = [
    { width: normalizedCritical, color: resolveColor(90) },
    { width: normalizedWarning - normalizedCritical, color: resolveColor(60) },
    { width: 100 - normalizedWarning, color: resolveColor(30) },
  ];
  const ticks = [0, 20, 40, 60, 80, 100];

  return (
    <Box
      sx={{
        flex: "0 0 200px",
        px: 2,
        py: 1.5,
        border: "1px solid #d8dbe8",
        borderRadius: "12px",
        backgroundColor: "#f6f7fb",
      }}
    >
      <Typography
        sx={{
          fontSize: 18,
          lineHeight: "22px",
          color: "#11131a",
          whiteSpace: "nowrap",
          mb: 1,
        }}
      >
        {formatMessage({ id: "pages.forecasting.summary.tci" })}:{" "}
        {value === null ? "—" : formatNumber(Math.round(value * 100) / 100)}
      </Typography>
      <Box>
        <Box sx={{ position: "relative" }}>
          <Stack
            direction="row"
            sx={{ height: 22, borderRadius: "6px", overflow: "hidden", border: "1px solid #cfd3e4" }}
          >
            {segments.map(segment => (
              <Box
                key={`${segment.color}-${segment.width}`}
                sx={{ width: `${segment.width}%`, backgroundColor: segment.color, opacity: 0.85 }}
              />
            ))}
          </Stack>
          {markerPosition !== null ? (
            <Box
              sx={{
                position: "absolute",
                left: `${markerPosition}%`,
                top: -4,
                bottom: -4,
                width: 4,
                borderRadius: 999,
                backgroundColor: "#3b4050",
                transform: "translateX(-50%)",
                boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.9)",
              }}
            />
          ) : null}
        </Box>
      </Box>
      <Box
        sx={{
          position: "relative",
          height: 18,
          mt: 0.75,
        }}
      >
        {ticks.map(tick => (
          <Typography
            key={tick}
            sx={{
              position: "absolute",
              left: `${tick}%`,
              top: 0,
              transform: tick === 0 ? "translateX(0)" : tick === 100 ? "translateX(-100%)" : "translateX(-50%)",
              fontSize: 12,
              lineHeight: "14px",
              color: "#4e5568",
              whiteSpace: "nowrap",
            }}
          >
            {tick}
          </Typography>
        ))}
      </Box>
      <FormControlLabel
        control={
          <Switch
            checked={isTrendPinned}
            inputProps={{ "aria-label": formatMessage({ id: "pages.forecasting.pinTciTrend" }) }}
            onChange={event => onTrendPinnedChange(event.target.checked)}
            size="small"
          />
        }
        label={
          <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#363846" }}>
            {formatMessage({ id: "pages.forecasting.pinTciTrend" })}
          </Typography>
        }
        sx={{ m: 0, mt: 0.5 }}
      />
    </Box>
  );
};

export const ForecastingSummaryPanel: FC<{
  isTrainTciTrendPinned: boolean;
  onTrainTciTrendPinnedChange: (value: boolean) => void;
}> = ({ isTrainTciTrendPinned, onTrainTciTrendPinnedChange }) => {
  const [searchParams] = useSearchParams();
  const { formatMessage, formatNumber } = useIntl();
  const {
    selectedItem,
    startTimestamp,
    endTimestamp,
    userSelectedRangeType,
    recorderWindowStart,
    recorderModeInterval,
    userSelectedRecorderModePeriod,
  } = useAsu();
  const settings = useSettings();
  const resolveAlarmColor = useAlarmPaletteResolver();

  const [status, setStatus] = useState(0);
  const [tci, setTci] = useState<number | null>(null);
  const [hasWorkTime, setHasWorkTime] = useState(false);
  const [workTimeHours, setWorkTimeHours] = useState<number | null>(null);
  const [weeklyAnomalySummary, setWeeklyAnomalySummary] = useState<IAnomalySummary>(getAnomalySummary(null));
  const [defectSummary, setDefectSummary] = useState<IDefectSummary>({
    worstStatus: 0,
    total: 0,
    low: 0,
    medium: 0,
    critical: 0,
  });

  const { warning, critical } = settings.getTCILevels();
  const selectedTrainId = selectedItem && "TrainId" in selectedItem ? normalizeTrainId(selectedItem.TrainId) : null;
  const trainId = selectedTrainId ?? normalizeTrainId(searchParams.get("trainId"));
  const trainName = selectedItem && "TrainName" in selectedItem ? selectedItem.TrainName ?? "" : "";
  const effectiveStartTimestamp =
    userSelectedRangeType === RangeType.RECORDER_MODE ? recorderWindowStart : startTimestamp;
  const weekStartTimestamp = buildForecastWeekStartTimestamp(endTimestamp);

  const { fn: getStatus, isLoading: isStatusLoading } = useApiCall(
    async (api, { idTrain, startTs, endTs }: { idTrain: number; startTs: string | Date; endTs: string | Date }) => {
      try {
        const json = (await api.fetchJson({
          url: `/trains/${idTrain}/status`,
          method: "GET",
          data: {
            startTimestamp: startTs,
            endTimestamp: endTs,
            useForAlert: true,
          },
        })) as { AlarmStatus: number | null } | null;

        return json?.AlarmStatus ?? 0;
      } catch (error) {
        console.error(error);
        return 0;
      }
    },
    { success: false, error: false },
  );

  const { fn: getTci, isLoading: isTciLoading } = useApiCall(
    async (api, { idTrain, startTs, endTs }: { idTrain: number; startTs: string | Date; endTs: string | Date }) => {
      try {
        const json = (await api.fetchJson({
          url: `/trains/${idTrain}/tci`,
          method: "GET",
          data: {
            startTimestamp: startTs,
            endTimestamp: endTs,
          },
        })) as { TCIVal: number | null } | null;

        return typeof json?.TCIVal === "number" ? json.TCIVal : null;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    { success: false, error: false },
  );

  const { fn: getWeeklyAnomalyData, isLoading: isWeeklyAnomalyLoading } = useApiCall(
    async (api, { idTrain, startTs, endTs }: { idTrain: number; startTs: string; endTs: string | Date }) => {
      try {
        const json = (await api.fetchJson({
          url: `/predictive/anomaly/${idTrain}`,
          method: "GET",
          data: {
            startTimestamp: startTs,
            endTimestamp: endTs,
          },
        })) as IAnomalyData | null;

        return json;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    { success: false, error: false },
  );

  const { fn: getDefects, isLoading: isDefectsLoading } = useApiCall(
    async (api, { idTrain, startTs, endTs }: { idTrain: number; startTs: string | Date; endTs: string | Date }) => {
      try {
        return (await api.fetchJson({
          url: `/predictive/defects/${idTrain}`,
          method: "GET",
          data: {
            startTimestamp: startTs,
            endTimestamp: endTs,
          },
        })) as IDefectsCountDataResponse;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    { success: false, error: false },
  );

  const { fn: getTrainPopup, isLoading: isTrainPopupLoading } = useApiCall(
    async (api, { idTrain }: { idTrain: number }) => {
      try {
        const json = (await api.fetchJson({
          url: `/journal/train/${idTrain}/popup`,
          method: "GET",
        })) as ITrainPopupResponse | null;

        const train = Array.isArray(json?.train) ? json.train[0] : null;

        return {
          hours: typeof train?.WorkTimeRecordedHours === "number" ? train.WorkTimeRecordedHours : null,
          hasValue:
            Boolean(train) &&
            (typeof train?.WorkTimeRecordedHours === "number" || Boolean(train?.WorkTimeRecordedDate)),
        };
      } catch (error) {
        console.error(error);
        return {
          hours: null,
          hasValue: false,
        };
      }
    },
    { success: false, error: false },
  );

  useEffect(() => {
    setStatus(0);
    setTci(null);
    setWeeklyAnomalySummary(getAnomalySummary(null));
    setDefectSummary({
      worstStatus: 0,
      total: 0,
      low: 0,
      medium: 0,
      critical: 0,
    });
  }, [trainId, userSelectedRangeType, recorderModeInterval, userSelectedRecorderModePeriod]);

  useEffect(() => {
    setHasWorkTime(false);
    setWorkTimeHours(null);
  }, [trainId]);

  useEffect(() => {
    if (!trainId) return;

    void getTrainPopup(undefined, { idTrain: trainId }).then(result => {
      if (!result || typeof result !== "object") return;

      setHasWorkTime(Boolean(result.hasValue));
      setWorkTimeHours(typeof result.hours === "number" ? result.hours : null);
    });
  }, [trainId]);

  useEffect(() => {
    if (!trainId) return;
    if (userSelectedRangeType === RangeType.RECORDER_MODE && !recorderWindowStart) return;
    if (!effectiveStartTimestamp || !weekStartTimestamp) return;

    const requests = [
      getStatus(undefined, { idTrain: trainId, startTs: effectiveStartTimestamp, endTs: endTimestamp }).then(
        nextStatus => {
          if (typeof nextStatus === "number") {
            setStatus(nextStatus);
          }
        },
      ),
      getTci(undefined, { idTrain: trainId, startTs: effectiveStartTimestamp, endTs: endTimestamp }).then(nextTci =>
        setTci(typeof nextTci === "number" ? nextTci : null),
      ),
      getWeeklyAnomalyData(undefined, { idTrain: trainId, startTs: weekStartTimestamp, endTs: endTimestamp }).then(
        data => setWeeklyAnomalySummary(getAnomalySummary(data ?? null)),
      ),
      getDefects(undefined, { idTrain: trainId, startTs: effectiveStartTimestamp, endTs: endTimestamp }).then(defects =>
        setDefectSummary(getDefectSummary(defects as IDefectsCountDataResponse)),
      ),
    ];

    void Promise.allSettled(requests);
  }, [trainId, endTimestamp, effectiveStartTimestamp, recorderWindowStart, userSelectedRangeType, weekStartTimestamp]);

  const loading = isStatusLoading || isTciLoading || isWeeklyAnomalyLoading || isDefectsLoading || isTrainPopupLoading;
  const formattedWorkTime =
    hasWorkTime && typeof workTimeHours === "number"
      ? formatNumber(Math.round(workTimeHours))
      : hasWorkTime
        ? formatNumber(0)
        : null;

  if (!trainId) {
    return null;
  }

  return (
    <Box
      sx={{
        flexShrink: 0,
        px: { xs: 1, md: 2 },
        pt: { xs: 1, md: 1.5 },
        pb: 1,
        opacity: loading ? 0.7 : 1,
        transition: "opacity 0.2s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            flex: "1 1 280px",
            minWidth: 280,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 1.5, md: 2 },
            py: { xs: 1.5, md: 2 },
            border: "1px solid #d8dbe8",
            borderRadius: "14px",
            background: "linear-gradient(180deg, #ffffff 0%, #f7f8fd 100%)",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 28, md: 34 },
              lineHeight: { xs: "32px", md: "38px" },
              color: "#11131a",
              minWidth: 0,
              wordBreak: "break-word",
            }}
          >
            {trainName}
          </Typography>
          {formattedWorkTime ? (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <AccessTime sx={{ color: "#8b90a2", fontSize: 28 }} />
              <Typography
                sx={{ fontSize: { xs: 22, md: 26 }, lineHeight: "28px", color: "#4b5161", whiteSpace: "nowrap" }}
              >
                {formattedWorkTime} {formatMessage({ id: "pages.forecasting.summary.hoursShort" })}
              </Typography>
            </Stack>
          ) : null}
        </Box>

        <Box
          sx={{
            flex: "1 1 320px",
            minWidth: 320,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 1,
            px: 2,
            py: 1.5,
            border: "1px solid #4a4f5f",
            borderRadius: "14px",
            backgroundColor: "#363846",
          }}
        >
          <SummaryRow
            label={formatMessage({ id: "pages.forecasting.summary.anomaliesWeek" })}
            statusColor={resolveAlarmColor(weeklyAnomalySummary.worstStatus)}
            total={weeklyAnomalySummary.total}
            critical={weeklyAnomalySummary.high}
            medium={weeklyAnomalySummary.medium}
            low={weeklyAnomalySummary.low}
          />
          <SummaryRow
            label={formatMessage({ id: "pages.forecasting.summary.defectsDetected" })}
            statusColor={resolveAlarmColor(defectSummary.worstStatus)}
            total={defectSummary.total}
            critical={defectSummary.critical}
            medium={defectSummary.medium}
            low={defectSummary.low}
          />
        </Box>

        <Box
          sx={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            py: 1.5,
            border: "1px solid #d8dbe8",
            borderRadius: "14px",
            backgroundColor: "#f1f2f6",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: 18, lineHeight: "22px", color: "#11131a" }}>
              {formatMessage({ id: "pages.forecasting.summary.gost" })}:
            </Typography>
            <StatusEye color={resolveAlarmColor(status)} size={26} />
          </Stack>
        </Box>
        <TciScale
          critical={critical}
          isTrendPinned={isTrainTciTrendPinned}
          onTrendPinnedChange={onTrainTciTrendPinnedChange}
          resolveColor={resolveAlarmColor}
          value={tci}
          warning={warning}
        />
      </Box>
    </Box>
  );
};
